'use client';

import * as React from 'react';
import { cx } from '../utils';
import { usePopover, Popover } from './Popover';
import { JIcon } from '../../icons';
import { devWarn } from '../internal/devWarn';
import type { StyledProps } from '../types';
import type { UnsafeStyleProps } from '../escape-hatches';

/* ── Shared date helpers ───────────────────────────── */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const WEEKDAYS_LONG = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function startDayOfWeek(year: number, month: number): number {
  return (new Date(year, month, 1).getDay() + 6) % 7;
}

function toDS(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function todayStr(): string {
  const t = new Date();
  return toDS(t.getFullYear(), t.getMonth(), t.getDate());
}

/** Dev-warns once per bad value, then returns the input if it's a
    valid ISO YYYY-MM-DD (calendar-correct), else `undefined`.
    Treat invalid `value` / `min` / `max` as "not supplied" rather than
    propagating impossible dates into the view machinery. */
const _seenBadIso = new Set<string>();
function safeISODate(s: string | undefined, label: string): string | undefined {
  if (s === undefined || s === '') return undefined;
  if (isISODate(s)) return s;
  if (!_seenBadIso.has(`${label}:${s}`)) {
    _seenBadIso.add(`${label}:${s}`);
    devWarn(
      `[JKit.DatePicker] ${label}="${s}" is not a valid ISO YYYY-MM-DD ` +
        '(impossible date or wrong shape); coerced to undefined.',
    );
  }
  return undefined;
}

/** MonthPicker analogue of safeISODate: dev-warns once, returns the input if
    it's a valid YYYY-MM (month 01-12), else `undefined`. Without this,
    MonthPicker rendered "undefined" into its label/aria-label for a malformed
    `value`, and an unpadded `min`/`max` corrupted the lexical bound compare. */
const _seenBadYm = new Set<string>();
function isYearMonth(s: string | undefined): s is string {
  return !!s && /^\d{4}-(0[1-9]|1[0-2])$/.test(s);
}
function safeYearMonth(s: string | undefined, label: string): string | undefined {
  if (s === undefined || s === '') return undefined;
  if (isYearMonth(s)) return s;
  if (!_seenBadYm.has(`${label}:${s}`)) {
    _seenBadYm.add(`${label}:${s}`);
    devWarn(
      `[JKit.MonthPicker] ${label}="${s}" is not a valid YYYY-MM ` +
        '(out-of-range month or wrong shape); coerced to undefined.',
    );
  }
  return undefined;
}

function isISODate(s: string | undefined): s is string {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  /* Shape-only regex accepts impossible dates ("2026-02-31", "2026-13-01",
     "2026-04-31"). Round-trip parse → format to enforce the calendar
     contract — JS Date silently normalizes invalid month-days (Feb 31
     becomes Mar 3), so we reject any string whose canonical form doesn't
     match the input. */
  const y = parseInt(s.slice(0, 4), 10);
  const m = parseInt(s.slice(5, 7), 10);
  const d = parseInt(s.slice(8, 10), 10);
  if (m < 1 || m > 12) return false;
  if (d < 1 || d > 31) return false;
  /* Build at noon UTC to avoid DST shifts pushing the day boundary. */
  const probe = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  return probe.getUTCFullYear() === y && probe.getUTCMonth() === m - 1 && probe.getUTCDate() === d;
}

/* Add months/years with the day clamped to the target month's length.
   Native Date arithmetic overflows invalid month-days (e.g. setMonth(1)
   on Jan 31 → Mar 3, because JS treats Feb 31 as Mar 3). PageDown /
   Shift+PageDown nav must land in the target month, not skip over it. */
function addMonthsClamped(
  year: number,
  month: number,
  day: number,
  deltaMonths: number,
): { year: number; month: number; day: number } {
  const total = month + deltaMonths;
  const targetMonth = ((total % 12) + 12) % 12;
  const targetYear = year + Math.floor(total / 12);
  const dim = daysInMonth(targetYear, targetMonth);
  return { year: targetYear, month: targetMonth, day: Math.min(day, dim) };
}

function addYearsClamped(
  year: number,
  month: number,
  day: number,
  deltaYears: number,
): { year: number; month: number; day: number } {
  const targetYear = year + deltaYears;
  const dim = daysInMonth(targetYear, month);
  return { year: targetYear, month, day: Math.min(day, dim) };
}

/* ── Shared sub-components ─────────────────────────── */

type CalLevel = 'days' | 'months' | 'years';

interface CalNavProps {
  onPrev: () => void;
  onNext: () => void;
  title: string;
  /** Omit to render the title as a static span (no drill-up). Used by
   *  standalone Calendar where there's no drill behavior — otherwise a
   *  focusable but no-op button would trap keyboard/SR users. */
  onTitleClick?: () => void;
  prevLabel?: string;
  nextLabel?: string;
  titleLabel?: string;
  /** Disable nav + title buttons. Used by standalone Calendar so a
   *  disabled calendar takes the nav controls out of the tab order
   *  along with the day cells (otherwise keyboard users could focus
   *  no-op Prev/Next/Title buttons in a disabled calendar). */
  disabled?: boolean;
}

function CalNav({
  onPrev,
  onNext,
  title,
  onTitleClick,
  prevLabel = 'Previous',
  nextLabel = 'Next',
  titleLabel,
  disabled = false,
}: CalNavProps) {
  return (
    <div className="j-datepicker-nav">
      <button
        type="button"
        className="j-datepicker-nav-btn"
        onClick={onPrev}
        aria-label={prevLabel}
        disabled={disabled}
      >
        <JIcon intent="chevron-left" size={14} strokeWidth={2.5} aria-hidden />
      </button>
      {onTitleClick ? (
        <button
          type="button"
          className="j-datepicker-nav-title"
          onClick={onTitleClick}
          aria-label={titleLabel}
          disabled={disabled}
        >
          {title}
        </button>
      ) : (
        <span
          className="j-datepicker-nav-title j-datepicker-nav-title-static"
          aria-label={titleLabel}
        >
          {title}
        </span>
      )}
      <button
        type="button"
        className="j-datepicker-nav-btn"
        onClick={onNext}
        aria-label={nextLabel}
        disabled={disabled}
      >
        <JIcon intent="chevron-right" size={14} strokeWidth={2.5} aria-hidden />
      </button>
    </div>
  );
}

interface YearGridProps {
  viewYear: number;
  onSelect: (year: number) => void;
}

function YearGrid({ viewYear, onSelect }: YearGridProps) {
  const base = Math.floor(viewYear / 12) * 12;
  const years = Array.from({ length: 12 }, (_, i) => base + i);
  return (
    <DrillGrid
      label="Years"
      items={years}
      cols={3}
      currentValue={viewYear}
      renderLabel={(y) => String(y)}
      onSelect={onSelect}
    />
  );
}

interface DrillGridProps<T extends number> {
  label: string;
  items: T[];
  cols: number;
  currentValue: T;
  renderLabel: (item: T) => string;
  isItemDisabled?: (item: T) => boolean;
  onSelect: (item: T) => void;
}

/** Reusable drill grid (months / years) with arrow-key navigation per APG. */
function DrillGrid<T extends number>({
  label,
  items,
  cols,
  currentValue,
  renderLabel,
  isItemDisabled,
  onSelect,
}: DrillGridProps<T>) {
  const refs = React.useRef<Map<T, HTMLButtonElement>>(new Map());
  const focus = (item: T) => refs.current.get(item)?.focus();
  const onKey = (e: React.KeyboardEvent, item: T) => {
    const idx = items.indexOf(item);
    if (idx < 0) return;
    let handled = true;
    let nextIdx = idx;
    if (e.key === 'ArrowRight') nextIdx = Math.min(items.length - 1, idx + 1);
    else if (e.key === 'ArrowLeft') nextIdx = Math.max(0, idx - 1);
    else if (e.key === 'ArrowDown') nextIdx = Math.min(items.length - 1, idx + cols);
    else if (e.key === 'ArrowUp') nextIdx = Math.max(0, idx - cols);
    else if (e.key === 'Home') nextIdx = 0;
    else if (e.key === 'End') nextIdx = items.length - 1;
    else if (e.key === 'Enter' || e.key === ' ') {
      if (!isItemDisabled?.(item)) onSelect(item);
    } else handled = false;
    if (handled) {
      e.preventDefault();
      if (nextIdx !== idx) focus(items[nextIdx]!);
    }
  };
  /* Roving tabindex: the selected cell is tabbable; if no selected
     cell is in the visible items (e.g. MonthPicker browsing a non-
     selected year, value-less DrillGrid open), fall back to the
     first enabled cell so Tab can still reach the grid. Without
     this, the entire grid is keyboard-unreachable. */
  const tabbableItem = (() => {
    if (items.includes(currentValue) && !(isItemDisabled?.(currentValue) ?? false)) {
      return currentValue;
    }
    return items.find((i) => !(isItemDisabled?.(i) ?? false));
  })();
  return (
    <div className="j-monthpicker-grid" role="grid" aria-label={label}>
      {chunk(items, cols).map((row, ri) => (
        <div key={ri} role="row" className="j-grid-row">
          {row.map((item) => {
            const selected = item === currentValue;
            const cellDisabled = isItemDisabled?.(item) ?? false;
            return (
              <button
                key={item}
                type="button"
                role="gridcell"
                ref={(el) => {
                  if (el) refs.current.set(item, el);
                  else refs.current.delete(item);
                }}
                className="j-monthpicker-cell"
                data-selected={selected || undefined}
                aria-selected={selected || undefined}
                aria-disabled={cellDisabled || undefined}
                disabled={cellDisabled}
                tabIndex={item === tabbableItem ? 0 : -1}
                onClick={() => !cellDisabled && onSelect(item)}
                onKeyDown={(e) => onKey(e, item)}
              >
                {renderLabel(item)}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function chunk<T>(arr: T[], n: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
  return out;
}

function navTitle(level: CalLevel, viewYear: number, viewMonth: number): string {
  if (level === 'days') return `${MONTHS[viewMonth]} ${viewYear}`;
  if (level === 'months') return `${viewYear}`;
  const base = Math.floor(viewYear / 12) * 12;
  return `${base}–${base + 11}`;
}

function navAriaTitle(level: CalLevel, viewYear: number, viewMonth: number): string {
  if (level === 'days') return `${MONTHS_LONG[viewMonth]} ${viewYear}, click to choose month`;
  if (level === 'months') return `${viewYear}, click to choose year`;
  const base = Math.floor(viewYear / 12) * 12;
  return `${base} to ${base + 11}`;
}

function gridLabel(viewYear: number, viewMonth: number): string {
  return `${MONTHS_LONG[viewMonth]} ${viewYear}`;
}

function dayCellLabel(year: number, month: number, day: number): string {
  const date = new Date(year, month, day);
  const weekday = WEEKDAYS_LONG[(date.getDay() + 6) % 7];
  return `${day} ${MONTHS_LONG[month]} ${year}, ${weekday}`;
}

/* ── DatePicker ────────────────────────────────────── */

export interface DatePickerProps extends StyledProps, UnsafeStyleProps {
  /** ISO date string `YYYY-MM-DD`. */
  value?: string;
  onChange?: (date: string) => void;
  placeholder?: string;
  /** ISO `YYYY-MM-DD` lower bound (inclusive). */
  min?: string;
  /** ISO `YYYY-MM-DD` upper bound (inclusive). */
  max?: string;
  disabled?: boolean;
  /** Adopter `aria-label` for the trigger button. Falls back to placeholder text. */
  'aria-label'?: string;
  /** External `aria-labelledby` for the trigger (e.g. parent Field label id). */
  'aria-labelledby'?: string;
  /** External `aria-describedby` for the trigger (parent help/error text id). */
  'aria-describedby'?: string;
  /** Maps to `data-jq-id` for QA hooks; convention `jq.date-picker.<variant>.<purpose>`. */
  testId?: string;
  /** Controls error border styling; pair with parent field text + icon for status. */
  error?: boolean;
  /** Visual height variant. Routes through ADR-045 `--control-h`. @default 'md' */
  size?: 'sm' | 'md' | 'lg';
}

export function DatePicker({
  value,
  onChange,
  placeholder = 'Select date',
  min,
  max,
  disabled = false,
  className,
  unsafe_style,
  error,
  size = 'md',
  testId,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
}: DatePickerProps) {
  const pop = usePopover();
  const popIsOpen = pop.isOpen;
  const closePopover = pop.close;
  const validValue = safeISODate(value, 'value');
  const safeMin = safeISODate(min, 'min');
  const safeMax = safeISODate(max, 'max');
  /* Audit #3 (2026-05-16): the initializers below previously called
     `new Date()` directly, which is non-deterministic across SSR ↔
     client (timezones + the request-time clock). React's hydration
     would flag the divergence on any route where DatePicker renders
     server-side. The fix: when no `value` is supplied, initialize to
     a deterministic placeholder (0/0) and use the effect to populate
     real today values after mount. The popover is closed by default,
     so users never see the placeholder year/month. */
  const [viewYear, setViewYear] = React.useState(() =>
    validValue ? parseInt(validValue.slice(0, 4)) : 0,
  );
  const [viewMonth, setViewMonth] = React.useState(() =>
    validValue ? parseInt(validValue.slice(5, 7)) - 1 : 0,
  );
  React.useEffect(() => {
    if (!validValue) {
      const now = new Date();
      setViewYear(now.getFullYear());
      setViewMonth(now.getMonth());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot post-mount initialization
  }, []);
  const [level, setLevel] = React.useState<CalLevel>('days');
  /** The day the user has navigated to in the grid (focus only — not the value). */
  const [focusedDS, setFocusedDS] = React.useState<string | null>(null);
  const [boundaryAnnounce, setBoundaryAnnounce] = React.useState('');
  const dayRefs = React.useRef<Map<string, HTMLButtonElement>>(new Map());

  React.useEffect(() => {
    if (validValue) {
      setViewYear(parseInt(validValue.slice(0, 4)));
      setViewMonth(parseInt(validValue.slice(5, 7)) - 1);
    }
  }, [validValue]);

  React.useEffect(() => {
    if (pop.isOpen) {
      setLevel('days');
      /* Seed the focused day so the restore effect moves DOM focus into the
         grid on open (instead of the popover's first focusable, the prev-month
         nav). Only seed an ENABLED day — seeding a disabled one would put
         roving focus on an unselectable cell (aria-disabled + aria-selected
         both true). Prefer the selected value, then today; if neither is in
         range, leave it null and the nav keeps focus. */
      const inRange = (ds: string) => !((safeMin && ds < safeMin) || (safeMax && ds > safeMax));
      const today = todayStr();
      setFocusedDS(validValue && inRange(validValue) ? validValue : inRange(today) ? today : null);
    }
  }, [pop.isOpen]);

  React.useEffect(() => {
    if (disabled && popIsOpen) closePopover();
  }, [disabled, popIsOpen, closePopover]);

  if (value && !validValue) {
    devWarn(
      `[JKit.DatePicker] value "${value}" is not ISO YYYY-MM-DD. ` +
        'Trigger will render placeholder; pass `YYYY-MM-DD` to render the value.',
    );
  }

  const today = todayStr();
  const isDisabled = (ds: string): boolean =>
    !!(safeMin && ds < safeMin) || !!(safeMax && ds > safeMax);

  const commit = (ds: string) => {
    if (disabled || isDisabled(ds)) return;
    onChange?.(ds);
    pop.close();
  };

  const selectMonth = (m: number) => {
    if (disabled) return;
    setViewMonth(m);
    setLevel('days');
  };
  const selectYear = (y: number) => {
    if (disabled) return;
    setViewYear(y);
    setLevel('months');
  };

  const prevNav = () => {
    if (disabled) return;
    if (level === 'days') {
      if (viewMonth === 0) {
        setViewMonth(11);
        setViewYear((y) => y - 1);
      } else setViewMonth((m) => m - 1);
    } else if (level === 'months') setViewYear((y) => y - 1);
    else setViewYear((y) => y - 12);
  };
  const nextNav = () => {
    if (disabled) return;
    if (level === 'days') {
      if (viewMonth === 11) {
        setViewMonth(0);
        setViewYear((y) => y + 1);
      } else setViewMonth((m) => m + 1);
    } else if (level === 'months') setViewYear((y) => y + 1);
    else setViewYear((y) => y + 12);
  };
  const drillUp = () => {
    if (disabled) return;
    if (level === 'days') setLevel('months');
    else if (level === 'months') setLevel('years');
  };

  /* Build day cells */
  const dim = daysInMonth(viewYear, viewMonth);
  const start = startDayOfWeek(viewYear, viewMonth);
  const prevDim = daysInMonth(viewYear, viewMonth === 0 ? 11 : viewMonth - 1);
  const cells: Array<{
    day: number;
    outside?: boolean;
    selected?: boolean;
    today?: boolean;
    disabled?: boolean;
    ds?: string;
  }> = [];
  for (let i = start - 1; i >= 0; i--) cells.push({ day: prevDim - i, outside: true });
  for (let d = 1; d <= dim; d++) {
    const ds = toDS(viewYear, viewMonth, d);
    cells.push({
      day: d,
      selected: ds === validValue,
      today: ds === today,
      disabled: isDisabled(ds),
      ds,
    });
  }
  /* Always render 42 cells (6 rows × 7 days) so the popover height
     is constant across months — Popover only measures position on
     open/resize, and switching from a 4-row Feb to a 6-row Jan
     mid-interaction would otherwise push the dialog off-screen. */
  while (cells.length < 42) cells.push({ day: cells.length - start - dim + 1, outside: true });

  /* Roving tabindex: focused (if in current month) > selected > today > first available */
  const focusedInMonth =
    focusedDS && focusedDS.slice(0, 7) === toDS(viewYear, viewMonth, 1).slice(0, 7)
      ? focusedDS
      : null;
  const tabbableDS = (() => {
    if (focusedInMonth) return focusedInMonth;
    const sel = cells.find((c) => c.selected && !c.disabled && !c.outside);
    if (sel) return sel.ds!;
    const tdy = cells.find((c) => c.today && !c.disabled && !c.outside);
    if (tdy) return tdy.ds!;
    const first = cells.find((c) => !c.disabled && !c.outside);
    return first?.ds;
  })();

  /* Move focus to a new day. Month and year jumps clamp the day to
     the target month length — JS Date overflow would otherwise skip
     into the next month (e.g. PageDown from Jan 31 → Mar 3 instead of
     Feb 28). Then walk past disabled cells in the requested direction
     so keyboard users don't get trapped on a min/max boundary.
     If we cross the month boundary, also change the view + focus after render. */
  const moveFocus = (current: string, deltaDays: number, jumpYear = false) => {
    const cur = new Date(current + 'T12:00:00');
    let y: number;
    let m: number;
    let d: number;
    if (jumpYear) {
      ({
        year: y,
        month: m,
        day: d,
      } = addYearsClamped(cur.getFullYear(), cur.getMonth(), cur.getDate(), deltaDays));
    } else if (Math.abs(deltaDays) >= 28 && deltaDays % 28 === 0) {
      ({
        year: y,
        month: m,
        day: d,
      } = addMonthsClamped(cur.getFullYear(), cur.getMonth(), cur.getDate(), deltaDays / 28));
    } else {
      /* Day-level adds — Date(year, month, day+delta) handles month
         overflow correctly (no day clamping needed). */
      const dt = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + deltaDays, 12, 0, 0);
      y = dt.getFullYear();
      m = dt.getMonth();
      d = dt.getDate();
    }
    /* Walk in the requested direction until we land on a non-disabled date,
       capped to ~1 year so a fully-disabled bound doesn't infinite-loop. */
    const step = deltaDays >= 0 ? 1 : -1;
    const cap = 366;
    for (let i = 0; i < cap; i++) {
      const ds = toDS(y, m, d);
      if (!isDisabled(ds)) {
        setFocusedDS(ds);
        if (y !== viewYear || m !== viewMonth) {
          setViewYear(y);
          setViewMonth(m);
        }
        return true;
      }
      const walked = new Date(y, m, d + step, 12, 0, 0);
      y = walked.getFullYear();
      m = walked.getMonth();
      d = walked.getDate();
    }
    return false;
  };

  const onGridKey = (e: React.KeyboardEvent) => {
    const cur = focusedInMonth || tabbableDS;
    if (!cur) return;
    let handled = true;
    let moved: boolean | undefined;
    let direction: 'earlier' | 'later' | null = null;
    if (e.key === 'ArrowLeft') {
      moved = moveFocus(cur, -1);
      direction = 'earlier';
    } else if (e.key === 'ArrowRight') {
      moved = moveFocus(cur, 1);
      direction = 'later';
    } else if (e.key === 'ArrowUp') {
      moved = moveFocus(cur, -7);
      direction = 'earlier';
    } else if (e.key === 'ArrowDown') {
      moved = moveFocus(cur, 7);
      direction = 'later';
    } else if (e.key === 'Home') {
      const d = new Date(cur + 'T12:00:00');
      const dow = (d.getDay() + 6) % 7;
      moveFocus(cur, -dow);
    } else if (e.key === 'End') {
      const d = new Date(cur + 'T12:00:00');
      const dow = (d.getDay() + 6) % 7;
      moveFocus(cur, 6 - dow);
    } else if (e.key === 'PageUp') {
      moved = moveFocus(cur, e.shiftKey ? -1 : -28, e.shiftKey);
      direction = 'earlier';
    } else if (e.key === 'PageDown') {
      moved = moveFocus(cur, e.shiftKey ? 1 : 28, e.shiftKey);
      direction = 'later';
    } else if (e.key === 'Enter' || e.key === ' ') {
      commit(cur);
    } else {
      handled = false;
    }
    if (handled) e.preventDefault();
    if (moved === false && direction) {
      setBoundaryAnnounce(
        direction === 'earlier' ? 'No earlier dates available.' : 'No later dates available.',
      );
    } else if (boundaryAnnounce) {
      setBoundaryAnnounce('');
    }
  };

  /* After view + focused day update, restore DOM focus to the new day. */
  React.useEffect(() => {
    if (!pop.isOpen || level !== 'days' || !focusedDS) return;
    const el = dayRefs.current.get(focusedDS);
    if (el) el.focus();
  }, [pop.isOpen, level, focusedDS, viewYear, viewMonth]);

  const displayValue = validValue
    ? new Date(validValue + 'T12:00:00').toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '';

  const triggerLabel = ariaLabel || (validValue ? `${displayValue}, change date` : placeholder);

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/role-supports-aria-props -- aria-invalid is a global ARIA attribute; jsx-a11y is conservative on implicit role="button" */}
      <button
        ref={pop.anchorRef as React.RefObject<HTMLButtonElement>}
        type="button"
        className={cx(
          'j-datepicker-trigger',
          size === 'sm' && 'j-datepicker-trigger-sm',
          size === 'lg' && 'j-datepicker-trigger-lg',
          error && 'j-datepicker-error',
          className,
        )}
        data-placeholder={!validValue ? true : undefined}
        data-jq-id={testId}
        disabled={disabled}
        onClick={pop.open}
        style={unsafe_style}
        aria-haspopup="dialog"
        aria-expanded={pop.isOpen}
        aria-label={ariaLabelledBy ? undefined : triggerLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-invalid={error || undefined}
      >
        <JIcon intent="calendar" size={16} strokeWidth={2} aria-hidden />
        {displayValue || placeholder}
      </button>
      <Popover {...pop.popoverProps} trapFocus panelRole={null}>
        <div
          className="j-datepicker-calendar"
          role="dialog"
          aria-label={`Choose date${validValue ? `, currently ${displayValue}` : ''}`}
          tabIndex={-1}
        >
          <CalNav
            onPrev={prevNav}
            onNext={nextNav}
            title={navTitle(level, viewYear, viewMonth)}
            titleLabel={navAriaTitle(level, viewYear, viewMonth)}
            onTitleClick={drillUp}
            prevLabel={
              level === 'days'
                ? 'Previous month'
                : level === 'months'
                  ? 'Previous year'
                  : 'Previous decade'
            }
            nextLabel={
              level === 'days' ? 'Next month' : level === 'months' ? 'Next year' : 'Next decade'
            }
          />
          <div className="j-datepicker-announce" role="status" aria-live="polite">
            {boundaryAnnounce}
          </div>
          {/* Keyed wrapper triggers j-fade entrance on every nav change.
              Focus restore (in the useEffect on [viewYear, viewMonth])
              re-runs after the remount so keyboard users land on the
              right cell. */}
          <div key={`${level}-${viewYear}-${viewMonth}`} className="j-datepicker-view">
            {level === 'years' && <YearGrid viewYear={viewYear} onSelect={selectYear} />}
            {level === 'months' && (
              <DrillGrid
                label="Months"
                items={Array.from({ length: 12 }, (_, i) => i)}
                cols={3}
                currentValue={viewMonth}
                renderLabel={(i) => MONTHS[i]!}
                onSelect={selectMonth}
              />
            )}
            {level === 'days' && (
              <>
                <div className="j-datepicker-weekdays" aria-hidden="true">
                  {WEEKDAYS.map((d) => (
                    <span key={d} className="j-datepicker-weekday">
                      {d}
                    </span>
                  ))}
                </div>
                <div
                  className="j-datepicker-grid"
                  role="grid"
                  aria-label={gridLabel(viewYear, viewMonth)}
                  tabIndex={-1}
                  onKeyDown={onGridKey}
                >
                  {chunk(
                    cells.map((c, i) => ({ c, i })),
                    7,
                  ).map((row, ri) => {
                    /* When a row is entirely outside-month padding (can
                       happen with the constant 6-row layout), drop the
                       `role="row"` so axe doesn't flag "row requires
                       gridcell children". Visual layout unchanged. */
                    const hasGridcell = row.some((entry) => !entry.c.outside);
                    return (
                      <div
                        key={ri}
                        role={hasGridcell ? 'row' : 'presentation'}
                        className="j-grid-row"
                      >
                        {row.map(({ c, i }) => {
                          const tabbable = c.ds && c.ds === tabbableDS;
                          return (
                            <button
                              key={i}
                              type="button"
                              role={c.outside ? 'presentation' : 'gridcell'}
                              ref={(el) => {
                                if (!c.ds) return;
                                if (el) dayRefs.current.set(c.ds, el);
                                else dayRefs.current.delete(c.ds);
                              }}
                              className="j-datepicker-day"
                              data-outside={c.outside || undefined}
                              data-today={c.today || undefined}
                              data-selected={c.selected || undefined}
                              aria-hidden={c.outside || undefined}
                              aria-selected={!c.outside ? !!c.selected : undefined}
                              aria-disabled={c.disabled || undefined}
                              aria-label={
                                !c.outside && c.ds
                                  ? dayCellLabel(viewYear, viewMonth, c.day)
                                  : undefined
                              }
                              disabled={c.outside || c.disabled}
                              tabIndex={tabbable ? 0 : -1}
                              onClick={c.outside || c.disabled ? undefined : () => commit(c.ds!)}
                              onFocus={!c.outside && c.ds ? () => setFocusedDS(c.ds!) : undefined}
                            >
                              {c.day}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </Popover>
    </>
  );
}

/* ── MonthPicker ───────────────────────────────────── */

export interface MonthPickerProps extends StyledProps, UnsafeStyleProps {
  /** Value format: 'YYYY-MM' */
  value?: string;
  onChange?: (month: string) => void;
  placeholder?: string;
  min?: string;
  max?: string;
  disabled?: boolean;
  'aria-label'?: string;
  testId?: string;
  error?: boolean;
  /** Visual height variant. Routes through ADR-045 `--control-h`. @default 'md' */
  size?: 'sm' | 'md' | 'lg';
}

export function MonthPicker({
  value,
  onChange,
  placeholder = 'Select month',
  min,
  max,
  disabled = false,
  className,
  unsafe_style,
  error,
  size = 'md',
  testId,
  'aria-label': ariaLabel,
}: MonthPickerProps) {
  const pop = usePopover();
  const popIsOpen = pop.isOpen;
  const closePopover = pop.close;
  /* Validate value/min/max up front (YYYY-MM): a malformed value otherwise
     renders "undefined <year>" into the label + aria-label, and an unpadded
     bound corrupts the lexical compare in isDisabled. */
  const safeValue = safeYearMonth(value, 'value');
  const safeMin = safeYearMonth(min, 'min');
  const safeMax = safeYearMonth(max, 'max');
  /* Audit #3: SSR-safe initializer. See same-named fix on DatePicker. */
  const [viewYear, setViewYear] = React.useState(() =>
    safeValue ? parseInt(safeValue.slice(0, 4)) : 0,
  );
  const [level, setLevel] = React.useState<'months' | 'years'>('months');
  const selMonth = safeValue ? parseInt(safeValue.slice(5, 7)) - 1 : -1;
  const selYear = safeValue ? parseInt(safeValue.slice(0, 4)) : -1;

  React.useEffect(() => {
    setViewYear(safeValue ? parseInt(safeValue.slice(0, 4)) : new Date().getFullYear());
  }, [safeValue]);

  React.useEffect(() => {
    if (pop.isOpen) setLevel('months');
  }, [pop.isOpen]);

  React.useEffect(() => {
    if (disabled && popIsOpen) closePopover();
  }, [disabled, popIsOpen, closePopover]);

  const isDisabled = (y: number, m: number): boolean => {
    const ym = `${y}-${String(m + 1).padStart(2, '0')}`;
    return !!(safeMin && ym < safeMin) || !!(safeMax && ym > safeMax);
  };

  const select = (m: number) => {
    if (disabled || isDisabled(viewYear, m)) return;
    onChange?.(`${viewYear}-${String(m + 1).padStart(2, '0')}`);
    pop.close();
  };
  const selectYear = (y: number) => {
    if (disabled) return;
    setViewYear(y);
    setLevel('months');
  };

  const prevNav = () => {
    if (disabled) return;
    if (level === 'months') setViewYear((y) => y - 1);
    else setViewYear((y) => y - 12);
  };
  const nextNav = () => {
    if (disabled) return;
    if (level === 'months') setViewYear((y) => y + 1);
    else setViewYear((y) => y + 12);
  };
  const drillUp = () => {
    if (disabled) return;
    if (level === 'months') setLevel('years');
  };

  const title =
    level === 'months'
      ? `${viewYear}`
      : `${Math.floor(viewYear / 12) * 12}–${Math.floor(viewYear / 12) * 12 + 11}`;
  const display = safeValue ? `${MONTHS[selMonth]} ${selYear}` : '';
  const triggerLabel = ariaLabel || (display ? `${display}, change month` : placeholder);

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/role-supports-aria-props -- aria-invalid is a global ARIA attribute; jsx-a11y is conservative on implicit role="button" */}
      <button
        ref={pop.anchorRef as React.RefObject<HTMLButtonElement>}
        type="button"
        className={cx(
          'j-datepicker-trigger',
          size === 'sm' && 'j-datepicker-trigger-sm',
          size === 'lg' && 'j-datepicker-trigger-lg',
          error && 'j-datepicker-error',
          className,
        )}
        data-placeholder={!safeValue ? true : undefined}
        data-jq-id={testId}
        disabled={disabled}
        onClick={pop.open}
        style={unsafe_style}
        aria-haspopup="dialog"
        aria-expanded={pop.isOpen}
        aria-label={triggerLabel}
        aria-invalid={error || undefined}
      >
        <JIcon intent="calendar" size={16} strokeWidth={2} aria-hidden />
        {display || placeholder}
      </button>
      <Popover {...pop.popoverProps} trapFocus panelRole={null}>
        <div
          className="j-datepicker-calendar j-datepicker-calendar-narrow"
          role="dialog"
          aria-label="Month picker"
        >
          <CalNav
            onPrev={prevNav}
            onNext={nextNav}
            title={title}
            onTitleClick={drillUp}
            prevLabel={level === 'months' ? 'Previous year' : 'Previous decade'}
            nextLabel={level === 'months' ? 'Next year' : 'Next decade'}
          />
          {/* Keyed wrapper triggers j-fade entrance on every nav. */}
          <div key={`${level}-${viewYear}`} className="j-datepicker-view">
            {level === 'years' ? (
              <YearGrid viewYear={viewYear} onSelect={selectYear} />
            ) : (
              <DrillGrid
                label={`Months in ${viewYear}`}
                items={Array.from({ length: 12 }, (_, i) => i)}
                cols={3}
                currentValue={viewYear === selYear ? selMonth : -1}
                renderLabel={(i) => MONTHS[i]!}
                isItemDisabled={(i) => isDisabled(viewYear, i)}
                onSelect={select}
              />
            )}
          </div>
        </div>
      </Popover>
    </>
  );
}

/* ── DateRangePicker ───────────────────────────────── */

export interface DateRange {
  start?: string;
  end?: string;
}

export interface DateRangePickerProps extends StyledProps, UnsafeStyleProps {
  value?: DateRange;
  onChange?: (range: DateRange) => void;
  placeholder?: string;
  min?: string;
  max?: string;
  disabled?: boolean;
  'aria-label'?: string;
  /** External `aria-labelledby` for the trigger (e.g. parent Field
   *  label id). When supplied, the kit-generated `aria-label` is
   *  suppressed so the external label takes precedence. */
  'aria-labelledby'?: string;
  /** External `aria-describedby` for the trigger (e.g. parent Field
   *  help/error message id). Critical for form-field composition —
   *  without this a screen-reader user hears "invalid" but never the
   *  associated message. Mirrors `DatePicker` / `MonthPicker`. */
  'aria-describedby'?: string;
  testId?: string;
  error?: boolean;
  /** Visual height variant. Routes through ADR-045 `--control-h`. @default 'md' */
  size?: 'sm' | 'md' | 'lg';
}

export function DateRangePicker({
  value = {},
  onChange,
  placeholder = 'Select range',
  min,
  max,
  disabled = false,
  className,
  unsafe_style,
  error,
  size = 'md',
  testId,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
}: DateRangePickerProps) {
  const pop = usePopover();
  const popIsOpen = pop.isOpen;
  const closePopover = pop.close;
  /* Hardened ISO validation: invalid value.start / value.end / min / max
     are coerced to undefined + dev-warn rather than propagated into
     view state. */
  const safeStart = safeISODate(value.start, 'value.start');
  const safeEnd = safeISODate(value.end, 'value.end');
  const safeMin = safeISODate(min, 'min');
  const safeMax = safeISODate(max, 'max');
  /* Audit #3: SSR-safe initializers. See DatePicker for full rationale. */
  const [viewYear, setViewYear] = React.useState(() => {
    const v = safeStart || safeEnd;
    return v ? parseInt(v.slice(0, 4)) : 0;
  });
  const [viewMonth, setViewMonth] = React.useState(() => {
    const v = safeStart || safeEnd;
    return v ? parseInt(v.slice(5, 7)) - 1 : 0;
  });
  React.useEffect(() => {
    const v = safeStart || safeEnd;
    if (!v) {
      const now = new Date();
      setViewYear(now.getFullYear());
      setViewMonth(now.getMonth());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot post-mount initialization
  }, []);
  const [level, setLevel] = React.useState<CalLevel>('days');
  const [picking, setPicking] = React.useState<string | null>(null);
  const [hoverDate, setHoverDate] = React.useState<string | null>(null);
  /* Keyboard focus inside the day grid (mirrors DatePicker's focusedDS
     pattern). Codex review #high: the range grid published role="grid"
     but had zero keyboard support — no onKeyDown, no roving tabindex,
     no Enter/Space contract. */
  const [focusedDS, setFocusedDS] = React.useState<string | null>(null);
  const dayRefs = React.useRef<Map<string, HTMLButtonElement>>(new Map());

  React.useEffect(() => {
    const v = safeStart || safeEnd;
    setViewYear(v ? parseInt(v.slice(0, 4)) : new Date().getFullYear());
    setViewMonth(v ? parseInt(v.slice(5, 7)) - 1 : new Date().getMonth());
  }, [safeStart, safeEnd]);

  React.useEffect(() => {
    if (pop.isOpen) {
      setLevel('days');
      setPicking(null);
      setHoverDate(null);
      setFocusedDS(null);
    }
  }, [pop.isOpen]);

  React.useEffect(() => {
    if (disabled && popIsOpen) closePopover();
  }, [disabled, popIsOpen, closePopover]);

  const today = todayStr();
  const isDisabled = (ds: string): boolean =>
    !!(safeMin && ds < safeMin) || !!(safeMax && ds > safeMax);

  const commitDay = (ds: string) => {
    if (disabled || isDisabled(ds)) return;
    if (!picking) {
      setPicking(ds);
      setHoverDate(null);
    } else {
      const [s, e] = picking <= ds ? [picking, ds] : [ds, picking];
      onChange?.({ start: s, end: e });
      setPicking(null);
      setHoverDate(null);
      setFocusedDS(null);
      pop.close();
    }
  };

  const handleDayClick = (day: number) => {
    const ds = toDS(viewYear, viewMonth, day);
    commitDay(ds);
  };

  const selectMonth = (m: number) => {
    if (disabled) return;
    setViewMonth(m);
    setLevel('days');
  };
  const selectYear = (y: number) => {
    if (disabled) return;
    setViewYear(y);
    setLevel('months');
  };

  const prevNav = () => {
    if (disabled) return;
    if (level === 'days') {
      if (viewMonth === 0) {
        setViewMonth(11);
        setViewYear((y) => y - 1);
      } else setViewMonth((m) => m - 1);
    } else if (level === 'months') setViewYear((y) => y - 1);
    else setViewYear((y) => y - 12);
  };
  const nextNav = () => {
    if (disabled) return;
    if (level === 'days') {
      if (viewMonth === 11) {
        setViewMonth(0);
        setViewYear((y) => y + 1);
      } else setViewMonth((m) => m + 1);
    } else if (level === 'months') setViewYear((y) => y + 1);
    else setViewYear((y) => y + 12);
  };
  const drillUp = () => {
    if (disabled) return;
    if (level === 'days') setLevel('months');
    else if (level === 'months') setLevel('years');
  };

  /* Build day cells with range highlighting. Preview "second anchor"
     prefers keyboard focus, falls back to pointer hover. */
  const dim = daysInMonth(viewYear, viewMonth);
  const start = startDayOfWeek(viewYear, viewMonth);
  const prevDim = daysInMonth(viewYear, viewMonth === 0 ? 11 : viewMonth - 1);
  /* Codex iter-5 #medium: previously `focusedDS || hoverDate` —
     after the first range click, focus stays on the clicked cell so
     focusedDS is non-null forever, masking subsequent mouse hovers.
     Pointer users lost the preview band. Reverse the priority:
     pointer hover wins when set (it's continuously refreshed by
     onMouseEnter), falling back to keyboard focus when no mouse
     activity is happening. Arrow keys clear hoverDate on the next
     keystroke (onGridKey) so keyboard nav still owns the preview. */
  const pointerAnchor = hoverDate || focusedDS;
  const cells: Array<{
    day: number;
    outside?: boolean;
    today?: boolean;
    disabled?: boolean;
    inRange?: boolean;
    isStart?: boolean;
    isEnd?: boolean;
    ds?: string;
  }> = [];
  for (let i = start - 1; i >= 0; i--) cells.push({ day: prevDim - i, outside: true });
  for (let d = 1; d <= dim; d++) {
    const ds = toDS(viewYear, viewMonth, d);
    const rangeStart = picking || safeStart;
    const rangeEnd = picking ? pointerAnchor || picking : safeEnd;
    const [rs, re] =
      rangeStart && rangeEnd && rangeStart <= rangeEnd
        ? [rangeStart, rangeEnd]
        : [rangeEnd, rangeStart];
    const inRange = !!(rs && re && ds >= rs && ds <= re);
    cells.push({
      day: d,
      today: ds === today,
      disabled: isDisabled(ds),
      inRange,
      isStart: ds === rs,
      isEnd: ds === re,
      ds,
    });
  }
  /* Always render 42 cells (6 rows × 7 days) so the popover height
     is constant across months — Popover only measures position on
     open/resize, and switching from a 4-row Feb to a 6-row Jan
     mid-interaction would otherwise push the dialog off-screen. */
  while (cells.length < 42) cells.push({ day: cells.length - start - dim + 1, outside: true });

  /* Roving tabindex: focused (if in current month) > picking anchor >
     start > today > first available. */
  const focusedInMonth =
    focusedDS && focusedDS.slice(0, 7) === toDS(viewYear, viewMonth, 1).slice(0, 7)
      ? focusedDS
      : null;
  const tabbableDS = (() => {
    if (focusedInMonth) return focusedInMonth;
    if (picking && picking.slice(0, 7) === toDS(viewYear, viewMonth, 1).slice(0, 7)) return picking;
    const startCell = cells.find((c) => c.isStart && !c.disabled && !c.outside);
    if (startCell) return startCell.ds!;
    const tdy = cells.find((c) => c.today && !c.disabled && !c.outside);
    if (tdy) return tdy.ds!;
    const first = cells.find((c) => !c.disabled && !c.outside);
    return first?.ds;
  })();

  /* Move focus inside the day grid; clamps month/year jumps via the
     shared helpers, walks past disabled cells, and returns whether
     a non-disabled landing site was found. */
  const moveFocus = (current: string, deltaDays: number, jumpYear = false) => {
    const cur = new Date(current + 'T12:00:00');
    let y: number;
    let m: number;
    let d: number;
    if (jumpYear) {
      ({
        year: y,
        month: m,
        day: d,
      } = addYearsClamped(cur.getFullYear(), cur.getMonth(), cur.getDate(), deltaDays));
    } else if (Math.abs(deltaDays) >= 28 && deltaDays % 28 === 0) {
      ({
        year: y,
        month: m,
        day: d,
      } = addMonthsClamped(cur.getFullYear(), cur.getMonth(), cur.getDate(), deltaDays / 28));
    } else {
      const dt = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + deltaDays, 12, 0, 0);
      y = dt.getFullYear();
      m = dt.getMonth();
      d = dt.getDate();
    }
    const step = deltaDays >= 0 ? 1 : -1;
    const cap = 366;
    for (let i = 0; i < cap; i++) {
      const ds = toDS(y, m, d);
      if (!isDisabled(ds)) {
        setFocusedDS(ds);
        if (y !== viewYear || m !== viewMonth) {
          setViewYear(y);
          setViewMonth(m);
        }
        return true;
      }
      const walked = new Date(y, m, d + step, 12, 0, 0);
      y = walked.getFullYear();
      m = walked.getMonth();
      d = walked.getDate();
    }
    return false;
  };

  const onGridKey = (e: React.KeyboardEvent) => {
    const cur = focusedInMonth || tabbableDS;
    if (!cur) return;
    let handled = true;
    if (e.key === 'ArrowLeft') moveFocus(cur, -1);
    else if (e.key === 'ArrowRight') moveFocus(cur, 1);
    else if (e.key === 'ArrowUp') moveFocus(cur, -7);
    else if (e.key === 'ArrowDown') moveFocus(cur, 7);
    else if (e.key === 'Home') {
      const d = new Date(cur + 'T12:00:00');
      const dow = (d.getDay() + 6) % 7;
      moveFocus(cur, -dow);
    } else if (e.key === 'End') {
      const d = new Date(cur + 'T12:00:00');
      const dow = (d.getDay() + 6) % 7;
      moveFocus(cur, 6 - dow);
    } else if (e.key === 'PageUp') {
      moveFocus(cur, e.shiftKey ? -1 : -28, e.shiftKey);
    } else if (e.key === 'PageDown') {
      moveFocus(cur, e.shiftKey ? 1 : 28, e.shiftKey);
    } else if (e.key === 'Enter' || e.key === ' ') {
      commitDay(cur);
    } else {
      handled = false;
    }
    if (handled) {
      e.preventDefault();
      /* Keyboard nav clears any stale pointer-hover anchor so the
         preview range follows keyboard focus, not a frozen hover
         from before the user switched modalities (Codex iter-5
         #medium). */
      if (hoverDate !== null) setHoverDate(null);
    }
  };

  /* After view + focused day update, restore DOM focus to the new day. */
  React.useEffect(() => {
    if (!pop.isOpen || level !== 'days' || !focusedDS) return;
    const el = dayRefs.current.get(focusedDS);
    if (el) el.focus();
  }, [pop.isOpen, level, focusedDS, viewYear, viewMonth]);

  const fmt = (ds?: string) =>
    ds
      ? new Date(ds + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
      : '';
  /* Normalize a reversed controlled value (start later than end) before
     formatting, so the trigger label agrees with the grid painting (which
     already orders the range). ISO YYYY-MM-DD strings compare correctly. */
  const [loDate, hiDate] =
    safeStart && safeEnd && safeStart > safeEnd ? [safeEnd, safeStart] : [safeStart, safeEnd];
  const displayValue =
    loDate && hiDate ? `${fmt(loDate)} – ${fmt(hiDate)}` : loDate ? `${fmt(loDate)} – …` : '';
  const triggerLabel = ariaLabel || (displayValue ? `${displayValue}, change range` : placeholder);

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/role-supports-aria-props -- aria-invalid is a global ARIA attribute; jsx-a11y is conservative on implicit role="button" */}
      <button
        ref={pop.anchorRef as React.RefObject<HTMLButtonElement>}
        type="button"
        className={cx(
          'j-datepicker-trigger',
          size === 'sm' && 'j-datepicker-trigger-sm',
          size === 'lg' && 'j-datepicker-trigger-lg',
          error && 'j-datepicker-error',
          className,
        )}
        data-placeholder={!safeStart ? true : undefined}
        data-jq-id={testId}
        disabled={disabled}
        onClick={pop.open}
        style={unsafe_style}
        aria-haspopup="dialog"
        aria-expanded={pop.isOpen}
        aria-label={ariaLabelledBy ? undefined : triggerLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-invalid={error || undefined}
      >
        <JIcon intent="calendar" size={16} strokeWidth={2} aria-hidden />
        {displayValue || placeholder}
      </button>
      <Popover {...pop.popoverProps} trapFocus panelRole={null}>
        <div
          className="j-datepicker-calendar j-datepicker-calendar-range"
          role="dialog"
          aria-label="Date range picker"
          tabIndex={-1}
        >
          {picking && (
            <div className="j-datepicker-range-hint" role="status" aria-live="polite">
              <JIcon intent="info" size={12} aria-hidden /> Select end date
            </div>
          )}
          <CalNav
            onPrev={prevNav}
            onNext={nextNav}
            title={navTitle(level, viewYear, viewMonth)}
            titleLabel={navAriaTitle(level, viewYear, viewMonth)}
            onTitleClick={drillUp}
            prevLabel={
              level === 'days'
                ? 'Previous month'
                : level === 'months'
                  ? 'Previous year'
                  : 'Previous decade'
            }
            nextLabel={
              level === 'days' ? 'Next month' : level === 'months' ? 'Next year' : 'Next decade'
            }
          />
          {/* Keyed wrapper triggers j-fade entrance on every nav. */}
          <div key={`${level}-${viewYear}-${viewMonth}`} className="j-datepicker-view">
            {level === 'years' && <YearGrid viewYear={viewYear} onSelect={selectYear} />}
            {level === 'months' && (
              <DrillGrid
                label="Months"
                items={Array.from({ length: 12 }, (_, i) => i)}
                cols={3}
                currentValue={viewMonth}
                renderLabel={(i) => MONTHS[i]!}
                onSelect={selectMonth}
              />
            )}
            {level === 'days' && (
              <>
                <div className="j-datepicker-weekdays" aria-hidden="true">
                  {WEEKDAYS.map((d) => (
                    <span key={d} className="j-datepicker-weekday">
                      {d}
                    </span>
                  ))}
                </div>
                <div
                  className="j-datepicker-grid"
                  role="grid"
                  aria-label={gridLabel(viewYear, viewMonth)}
                  tabIndex={-1}
                  onKeyDown={onGridKey}
                >
                  {chunk(
                    cells.map((c, i) => ({ c, i })),
                    7,
                  ).map((row, ri) => {
                    /* When a row is entirely outside-month padding (can
                       happen with the constant 6-row layout), drop the
                       `role="row"` so axe doesn't flag "row requires
                       gridcell children". Visual layout unchanged. */
                    const hasGridcell = row.some((entry) => !entry.c.outside);
                    return (
                      <div
                        key={ri}
                        role={hasGridcell ? 'row' : 'presentation'}
                        className="j-grid-row"
                      >
                        {row.map(({ c, i }) => {
                          const tabbable = c.ds && c.ds === tabbableDS;
                          return (
                            <button
                              key={i}
                              type="button"
                              role={c.outside ? 'presentation' : 'gridcell'}
                              ref={(el) => {
                                if (!c.ds) return;
                                if (el) dayRefs.current.set(c.ds, el);
                                else dayRefs.current.delete(c.ds);
                              }}
                              className="j-datepicker-day"
                              data-outside={c.outside || undefined}
                              data-today={c.today || undefined}
                              data-selected={c.isStart || c.isEnd || undefined}
                              data-in-range={c.inRange || undefined}
                              aria-hidden={c.outside || undefined}
                              aria-selected={!c.outside ? !!(c.isStart || c.isEnd) : undefined}
                              aria-disabled={c.disabled || undefined}
                              aria-label={
                                !c.outside && c.ds
                                  ? dayCellLabel(viewYear, viewMonth, c.day)
                                  : undefined
                              }
                              disabled={c.outside || c.disabled}
                              tabIndex={tabbable ? 0 : -1}
                              onMouseEnter={
                                !c.outside && picking ? () => setHoverDate(c.ds!) : undefined
                              }
                              onClick={
                                c.outside || c.disabled ? undefined : () => handleDayClick(c.day)
                              }
                              onFocus={!c.outside && c.ds ? () => setFocusedDS(c.ds!) : undefined}
                            >
                              {c.day}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </Popover>
    </>
  );
}

/* ── Calendar (standalone, no popover) ─────────────── */

export interface CalendarProps extends StyledProps, UnsafeStyleProps {
  /** ISO `YYYY-MM-DD`. */
  value?: string;
  onChange?: (date: string) => void;
  /** Initial month/year when uncontrolled / no value. */
  defaultViewYear?: number;
  defaultViewMonth?: number;
  min?: string;
  max?: string;
  disabled?: boolean;
  'aria-label'?: string;
  testId?: string;
}

export function Calendar({
  value,
  onChange,
  defaultViewYear,
  defaultViewMonth,
  min,
  max,
  disabled = false,
  className,
  unsafe_style,
  testId,
  'aria-label': ariaLabel,
}: CalendarProps) {
  const validValue = safeISODate(value, 'value');
  const safeMin = safeISODate(min, 'min');
  const safeMax = safeISODate(max, 'max');
  /* Codex iter-6 #high: defaultViewYear / defaultViewMonth were
     accepted verbatim. A caller passing `12` or `-1` (zero-based
     overflow) or a 1-based value from serialized data put the grid
     into an out-of-range month state — toDS would later emit
     non-ISO strings like "2026-13-01" through onChange. Validate
     both inputs as integers in their respective ranges; coerce
     invalid values to undefined + dev-warn so the fallback (current
     month/year, or value-derived) takes over. */
  const safeDefaultViewYear = (() => {
    if (defaultViewYear === undefined) return undefined;
    if (Number.isInteger(defaultViewYear) && defaultViewYear >= 1 && defaultViewYear <= 9999) {
      return defaultViewYear;
    }
    devWarn(
      `[JKit.Calendar] defaultViewYear=${defaultViewYear} must be an integer in [1..9999]; coerced.`,
    );
    return undefined;
  })();
  const safeDefaultViewMonth = (() => {
    if (defaultViewMonth === undefined) return undefined;
    if (Number.isInteger(defaultViewMonth) && defaultViewMonth >= 0 && defaultViewMonth <= 11) {
      return defaultViewMonth;
    }
    devWarn(
      `[JKit.Calendar] defaultViewMonth=${defaultViewMonth} must be a zero-based ` +
        'integer in [0..11] (Jan=0, Dec=11); coerced.',
    );
    return undefined;
  })();
  /* Audit #3 (2026-05-16): Calendar (standalone) is rendered inline,
     unlike DatePicker / MonthPicker / DateRangePicker whose popover
     hides the SSR sentinel. Calendar needs a different strategy:
     viewYear/viewMonth stay non-nullable (so all the math below works),
     but a separate `viewInitialized` flag gates the JSX. While false,
     render a placeholder div of the same outer shape; once true (post-
     mount), render the real Calendar. The placeholder + real both share
     `.j-datepicker-calendar .j-calendar` classnames so hydration sees
     an identical container and the swap is invisible.
     Codex round 1 on PR #77 caught that a 0/0 sentinel was user-visible
     in standalone <Calendar /> with no value/defaultView. */
  const eagerYear: number | undefined =
    safeDefaultViewYear ?? (validValue ? parseInt(validValue.slice(0, 4)) : undefined);
  const eagerMonth: number | undefined =
    safeDefaultViewMonth ?? (validValue ? parseInt(validValue.slice(5, 7)) - 1 : undefined);
  const [viewYear, setViewYear] = React.useState<number>(eagerYear ?? 0);
  const [viewMonth, setViewMonth] = React.useState<number>(eagerMonth ?? 0);
  /* `viewInitialized` is true when at least one axis came from a real
     source (value / defaultView). If both axes are missing, defer the
     render and let the post-mount effect populate. Each axis is then
     populated independently — codex round 1 caught that the prior
     compound guard skipped one axis when only the other had a default. */
  const [viewInitialized, setViewInitialized] = React.useState<boolean>(
    eagerYear != null && eagerMonth != null,
  );
  React.useEffect(() => {
    const now = new Date();
    if (eagerYear == null) setViewYear(now.getFullYear());
    if (eagerMonth == null) setViewMonth(now.getMonth());
    setViewInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot post-mount initialization
  }, []);
  const [focusedDS, setFocusedDS] = React.useState<string | null>(null);
  const [boundaryAnnounce, setBoundaryAnnounce] = React.useState('');
  const dayRefs = React.useRef<Map<string, HTMLButtonElement>>(new Map());

  React.useEffect(() => {
    if (validValue) {
      setViewYear(parseInt(validValue.slice(0, 4)));
      setViewMonth(parseInt(validValue.slice(5, 7)) - 1);
    }
  }, [validValue]);

  const today = todayStr();
  const isDisabledDS = (ds: string) => !!(safeMin && ds < safeMin) || !!(safeMax && ds > safeMax);

  const prev = () => {
    if (disabled) return;
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else setViewMonth((m) => m - 1);
  };
  const next = () => {
    if (disabled) return;
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else setViewMonth((m) => m + 1);
  };

  const dim = daysInMonth(viewYear, viewMonth);
  const start = startDayOfWeek(viewYear, viewMonth);
  const prevDim = daysInMonth(viewYear, viewMonth === 0 ? 11 : viewMonth - 1);
  const cells: Array<{
    day: number;
    outside?: boolean;
    selected?: boolean;
    today?: boolean;
    disabled?: boolean;
    ds?: string;
  }> = [];
  for (let i = start - 1; i >= 0; i--) cells.push({ day: prevDim - i, outside: true });
  for (let d = 1; d <= dim; d++) {
    const ds = toDS(viewYear, viewMonth, d);
    cells.push({
      day: d,
      selected: ds === validValue,
      today: ds === today,
      disabled: isDisabledDS(ds),
      ds,
    });
  }
  /* Always render 42 cells (6 rows × 7 days) so the popover height
     is constant across months — Popover only measures position on
     open/resize, and switching from a 4-row Feb to a 6-row Jan
     mid-interaction would otherwise push the dialog off-screen. */
  while (cells.length < 42) cells.push({ day: cells.length - start - dim + 1, outside: true });

  const focusedInMonth =
    focusedDS && focusedDS.slice(0, 7) === toDS(viewYear, viewMonth, 1).slice(0, 7)
      ? focusedDS
      : null;
  const tabbableDS = (() => {
    if (focusedInMonth) return focusedInMonth;
    const sel = cells.find((c) => c.selected && !c.disabled && !c.outside);
    if (sel) return sel.ds!;
    const tdy = cells.find((c) => c.today && !c.disabled && !c.outside);
    if (tdy) return tdy.ds!;
    const first = cells.find((c) => !c.disabled && !c.outside);
    return first?.ds;
  })();

  /* Clamp the target day to the destination month / year length so
     PageDown / Shift+PageDown from a month-end date doesn't overflow
     into the following month (Jan 31 → Feb 28, not Mar 3). */
  const moveFocus = (current: string, deltaDays: number, jumpYear = false) => {
    const cur = new Date(current + 'T12:00:00');
    let y: number;
    let m: number;
    let d: number;
    if (jumpYear) {
      ({
        year: y,
        month: m,
        day: d,
      } = addYearsClamped(cur.getFullYear(), cur.getMonth(), cur.getDate(), deltaDays));
    } else if (Math.abs(deltaDays) >= 28 && deltaDays % 28 === 0) {
      ({
        year: y,
        month: m,
        day: d,
      } = addMonthsClamped(cur.getFullYear(), cur.getMonth(), cur.getDate(), deltaDays / 28));
    } else {
      const dt = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + deltaDays, 12, 0, 0);
      y = dt.getFullYear();
      m = dt.getMonth();
      d = dt.getDate();
    }
    const step = deltaDays >= 0 ? 1 : -1;
    const cap = 366;
    for (let i = 0; i < cap; i++) {
      const ds = toDS(y, m, d);
      if (!isDisabledDS(ds)) {
        setFocusedDS(ds);
        if (y !== viewYear || m !== viewMonth) {
          setViewYear(y);
          setViewMonth(m);
        }
        return true;
      }
      const walked = new Date(y, m, d + step, 12, 0, 0);
      y = walked.getFullYear();
      m = walked.getMonth();
      d = walked.getDate();
    }
    return false;
  };

  const onGridKey = (e: React.KeyboardEvent) => {
    const cur = focusedInMonth || tabbableDS;
    if (!cur) return;
    let handled = true;
    let moved: boolean | undefined;
    let direction: 'earlier' | 'later' | null = null;
    if (e.key === 'ArrowLeft') {
      moved = moveFocus(cur, -1);
      direction = 'earlier';
    } else if (e.key === 'ArrowRight') {
      moved = moveFocus(cur, 1);
      direction = 'later';
    } else if (e.key === 'ArrowUp') {
      moved = moveFocus(cur, -7);
      direction = 'earlier';
    } else if (e.key === 'ArrowDown') {
      moved = moveFocus(cur, 7);
      direction = 'later';
    } else if (e.key === 'Home') {
      const d = new Date(cur + 'T12:00:00');
      const dow = (d.getDay() + 6) % 7;
      moveFocus(cur, -dow);
    } else if (e.key === 'End') {
      const d = new Date(cur + 'T12:00:00');
      const dow = (d.getDay() + 6) % 7;
      moveFocus(cur, 6 - dow);
    } else if (e.key === 'PageUp') {
      moved = moveFocus(cur, e.shiftKey ? -1 : -28, e.shiftKey);
      direction = 'earlier';
    } else if (e.key === 'PageDown') {
      moved = moveFocus(cur, e.shiftKey ? 1 : 28, e.shiftKey);
      direction = 'later';
    } else if (e.key === 'Enter' || e.key === ' ') {
      if (!isDisabledDS(cur)) onChange?.(cur);
    } else {
      handled = false;
    }
    if (handled) e.preventDefault();
    if (moved === false && direction) {
      setBoundaryAnnounce(
        direction === 'earlier' ? 'No earlier dates available.' : 'No later dates available.',
      );
    } else if (boundaryAnnounce) {
      setBoundaryAnnounce('');
    }
  };

  React.useEffect(() => {
    if (!focusedDS) return;
    const el = dayRefs.current.get(focusedDS);
    if (el) el.focus();
  }, [focusedDS, viewYear, viewMonth]);

  /* SSR sentinel guard: short-circuit rendering until the post-mount
     effect populates viewYear/viewMonth (or eager defaults provide them
     synchronously). Render a deterministic SKELETON with the full
     outer shape — nav bar, weekday strip, 6×7 grid of placeholder
     cells — so the layout doesn't collapse and there's no visible CLS
     when the real calendar swaps in. Codex round 3 caught that a bare
     placeholder div shrank to 0 height because the shared
     `.j-datepicker-calendar` classes don't reserve grid space. */
  if (!viewInitialized) {
    const PLACEHOLDER_CELLS = Array.from({ length: 42 });
    return (
      <div
        className={cx('j-datepicker-calendar j-calendar', className)}
        style={unsafe_style}
        data-jq-id={testId}
        role="group"
        aria-busy="true"
        aria-disabled={disabled || undefined}
      >
        {/* Skeleton nav row preserves header height. */}
        <div className="j-datepicker-nav" aria-hidden="true">
          <span className="j-datepicker-nav-btn" />
          <span className="j-datepicker-nav-title j-datepicker-nav-title-static">&nbsp;</span>
          <span className="j-datepicker-nav-btn" />
        </div>
        {/* Skeleton grid preserves the 6-row body height + weekday strip. */}
        <div className="j-datepicker-view" aria-hidden="true">
          <div className="j-datepicker-weekdays">
            {WEEKDAYS.map((d) => (
              <span key={d} className="j-datepicker-weekday">
                {d}
              </span>
            ))}
          </div>
          <div className="j-datepicker-grid">
            {PLACEHOLDER_CELLS.map((_, i) => (
              <span key={i} className="j-datepicker-day" data-outside="true" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cx('j-datepicker-calendar j-calendar', className)}
      style={unsafe_style}
      data-jq-id={testId}
      role="group"
      aria-label={ariaLabel || `Calendar, ${MONTHS_LONG[viewMonth]} ${viewYear}`}
      aria-disabled={disabled || undefined}
    >
      <CalNav
        onPrev={prev}
        onNext={next}
        title={`${MONTHS[viewMonth]} ${viewYear}`}
        titleLabel={`${MONTHS_LONG[viewMonth]} ${viewYear}`}
        /* Calendar has no drill-up — render title as static span. */
        prevLabel="Previous month"
        nextLabel="Next month"
        disabled={disabled}
      />
      <div className="j-datepicker-announce" role="status" aria-live="polite">
        {boundaryAnnounce}
      </div>
      {/* Keyed wrapper triggers j-fade entrance on each month nav. */}
      <div key={`${viewYear}-${viewMonth}`} className="j-datepicker-view">
        <div className="j-datepicker-weekdays" aria-hidden="true">
          {WEEKDAYS.map((d) => (
            <span key={d} className="j-datepicker-weekday">
              {d}
            </span>
          ))}
        </div>
        <div
          className="j-datepicker-grid"
          role="grid"
          aria-label={gridLabel(viewYear, viewMonth)}
          tabIndex={-1}
          onKeyDown={onGridKey}
        >
          {chunk(
            cells.map((c, i) => ({ c, i })),
            7,
          ).map((row, ri) => {
            const hasGridcell = row.some((entry) => !entry.c.outside);
            return (
              <div key={ri} role={hasGridcell ? 'row' : 'presentation'} className="j-grid-row">
                {row.map(({ c, i }) => {
                  const tabbable = c.ds && c.ds === tabbableDS;
                  return (
                    <button
                      key={i}
                      type="button"
                      role={c.outside ? 'presentation' : 'gridcell'}
                      ref={(el) => {
                        if (!c.ds) return;
                        if (el) dayRefs.current.set(c.ds, el);
                        else dayRefs.current.delete(c.ds);
                      }}
                      className="j-datepicker-day"
                      data-outside={c.outside || undefined}
                      data-today={c.today || undefined}
                      data-selected={c.selected || undefined}
                      aria-hidden={c.outside || undefined}
                      aria-selected={!c.outside ? !!c.selected : undefined}
                      aria-disabled={c.disabled || undefined}
                      aria-label={
                        !c.outside && c.ds ? dayCellLabel(viewYear, viewMonth, c.day) : undefined
                      }
                      disabled={disabled || c.outside || c.disabled}
                      tabIndex={tabbable ? 0 : -1}
                      onClick={
                        c.outside || c.disabled || disabled ? undefined : () => onChange?.(c.ds!)
                      }
                      onFocus={!c.outside && c.ds ? () => setFocusedDS(c.ds!) : undefined}
                    >
                      {c.day}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
